/**
 * 조건 칩 줄(`HubConditionBar`)이 **접힌 채로 적는 말과 세는 수** — 순수 변환이다.
 *
 * 🔑 Research 소셜 · 브랜드 멘션 추적 · 타겟 멘션 추적이 같은 말투를 쓴다 — 칩의 이름표는 사이드바
 *   섹션(`FilterBasic`)과 **같은 값**을 읽고, 값을 바꾸는 쪽은 언제나 그 섹션이다.
 * ⚠️ 화면마다 「초기화」가 되돌리는 범위가 달라 걸린 수(`narrowed`)는 화면이 센다 — 여기는 칸 하나의 말만 만든다.
 * ⚠️ `'use client'` 를 붙이지 않는다.
 */

import type { BrandPersonaToken } from 'src/utils/brand/brand-persona-persist';

import { fNumber } from 'src/utils/format/format-number';
import { SORT_TYPES } from 'src/utils/moments/view/post-sort';
import {
  getMainPlatforms,
  DEFAULT_X_SUBTYPE_PLATFORMS,
  DEFAULT_TIKTOK_AD_TYPE_CHIPS,
  DEFAULT_TIKTOK_AD_BOOST_CHIPS,
  DEFAULT_INSTAGRAM_SUBTYPE_PLATFORMS,
} from 'src/utils/brand/brand-filter-utils';

import { languageMap } from 'src/constants/languages';
import { periodLabelOf } from 'src/constants/plan-period';
import platformCatalog from 'src/constants/platform-catalog';
import { contentLanguageMap } from 'src/constants/content-languages';

import { summarizeSelection } from 'src/components/cmui/hub/hub-condition-summary';

/** 페르소나 칩 셋 — 사이드바 페르소나 섹션의 표기 */
const PERSONA_LABEL = { general: '일반', ads: '광고/협찬', official: '브랜드 계정' } as const;

/** 저장 토큰(`brand-persona-persist`)으로 고르는 자리의 선택지 — 표기는 위 칩 셋 그대로 · 차례는 토큰 차례 */
export const PERSONA_TOKEN_OPTIONS: readonly { value: BrandPersonaToken; label: string }[] = [
  { value: 'organic', label: PERSONA_LABEL.general },
  { value: 'sponsored', label: PERSONA_LABEL.ads },
  { value: 'brandAccount', label: PERSONA_LABEL.official },
];

/** 토큰 하나의 표기 — 모르는 토큰은 그대로 */
export function personaTokenLabel(token: string): string {
  return PERSONA_TOKEN_OPTIONS.find((option) => option.value === token)?.label ?? token;
}

/**
 * 정렬 여섯 — 사이드바 「정렬기준」 칩(`filter-sort-section`)과 **같은 값·같은 차례**다.
 * ⚠️ 그쪽 칩 목록이 바뀌면 여기도 바꾼다 — 칩 줄과 사이드바가 다른 정렬을 고르게 된다.
 * ⚠️ 검색(Research)의 정렬은 둘뿐이다 — 이 목록을 쓰지 않는다.
 */
export const MOMENT_SORT_OPTIONS = [
  { value: SORT_TYPES.POPULAR, label: '총 도달' },
  { value: SORT_TYPES.LATEST, label: '최신순' },
  { value: SORT_TYPES.COMMENT, label: '댓글순' },
  { value: SORT_TYPES.LIKE, label: '좋아요순' },
  { value: SORT_TYPES.VIEW, label: '조회순' },
  { value: SORT_TYPES.ER, label: 'E/R순' },
];

type DayLike = { format: (pattern: string) => string } | null | undefined;

/** 페르소나 칸 — 값이 **배열 꼴**(`adsEval: [true]`)과 **낱값 꼴**(`adsEval: true`) 두 벌로 온다 */
export type PersonaFilterValue = {
  adsEval?: unknown;
  isOfficialAccount?: unknown;
};

/** 값이 배열이든 문자열 하나든 목록으로 */
export function toFilterCodes(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(String);
  return value ? [String(value)] : [];
}

/**
 * 기간 칩의 말 — 빠른 기간이면 「최근 7일」, 직접 고른 범위면 「09.01~09.07」.
 *
 * @param quickPeriodDays 고른 빠른 기간(일) — `null` 이면 직접입력
 * @param start 시작일(dayjs)
 * @param end 끝일(dayjs)
 */
export function toFilterPeriodLabel(quickPeriodDays: number | null | undefined, start: DayLike, end: DayLike): string {
  if (quickPeriodDays != null) return periodLabelOf(quickPeriodDays);
  if (!start || !end) return '직접입력';
  return `${start.format('MM.DD')}~${end.format('MM.DD')}`;
}

/** 채널 칩의 말 — 고른 채널이 없으면 「전체」(세부 채널 칩은 세지 않는다) */
export function toFilterPlatformLabel(platforms: unknown): string {
  const options = platformCatalog.map((one) => ({ value: one.id, label: one.label }));
  return summarizeSelection(getMainPlatforms(platforms), options, '전체');
}

/** 국가 칩의 말 — 코드를 한국어 이름으로 */
export function toFilterCountryLabel(country: unknown): string {
  const options = languageMap.map((one) => ({ value: one.code, label: one.ko || one.code }));
  return summarizeSelection(toFilterCodes(country), options, '전체');
}

/** 언어 칩의 말 */
export function toFilterLanguageLabel(languages: unknown): string {
  const options = contentLanguageMap.map((one) => ({ value: one.code, label: one.name }));
  return summarizeSelection(toFilterCodes(languages), options, '전체');
}

/**
 * 고른 페르소나 이름들 — 사이드바 섹션과 같은 규칙으로 읽는다.
 * @returns 빈 목록이면 「전체」(아무것도 안 걸림) — 셋 다 고른 것도 빈 목록이다
 */
export function selectedPersonaLabels(filter: PersonaFilterValue): string[] {
  const ads = filter.adsEval;
  const official = filter.isOfficialAccount;
  const adsValues: unknown[] = Array.isArray(ads) ? ads : (ads === true || ads === false ? [ads] : []);
  const isOfficial = Array.isArray(official) ? official.includes(true) : Boolean(official);

  const labels = [
    ...(adsValues.includes(false) ? [PERSONA_LABEL.general] : []),
    ...(adsValues.includes(true) ? [PERSONA_LABEL.ads] : []),
    ...(isOfficial ? [PERSONA_LABEL.official] : []),
  ];
  // 셋 다 고른 것은 아무것도 안 고른 것과 같은 뜻이다
  return labels.length === 3 ? [] : labels;
}

/** 작성자(페르소나) 칩의 말 */
export function toFilterPersonaLabel(filter: PersonaFilterValue): string {
  const labels = selectedPersonaLabels(filter);
  return summarizeSelection(labels, labels.map((one) => ({ value: one, label: one })), '전체');
}

/**
 * 상세 조건(최소 도달 · 결과 내 재검색)에 걸린 수.
 * @param filter 화면의 필터 — 최소 도달은 비어 있으면(`null`) 하한이 없다(0 과 다르다)
 */
export function countFilterDetail(filter: { metricMin?: unknown; refineText?: unknown }): number {
  return (filter.metricMin != null ? 1 : 0) + (String(filter.refineText ?? '').trim() ? 1 : 0);
}

/**
 * 최소 도달 칩의 말 — 「1,000 이상」, 걸린 하한이 없으면 「없음」.
 * ⚠️ 0 도 「없음」으로 적는다 — 도달은 음수가 없어 0 이상은 아무것도 거르지 않는다(세트 저장값이 0 으로 오는 길이 있다).
 */
export function toFilterMetricMinLabel(metricMin: unknown): string {
  if (metricMin == null || metricMin === '') return '없음';
  const n = Number(metricMin);
  return Number.isFinite(n) && n > 0 ? `${fNumber(n)} 이상` : '없음';
}

/** 결과 내 재검색 칩의 말 — 넣은 말 그대로, 비어 있으면 「전체」 */
export function toFilterRefineLabel(refineText: unknown): string {
  const text = String(refineText ?? '').trim();
  return text || '전체';
}

/** 「수집 기준」 같은 켜고 끄는 칸 하나 — 지금 값과 처음 값 */
export type FilterToggleState = { label: string; on: boolean; defaultOn: boolean };

/**
 * 켜고 끄는 칸 묶음의 접힌 말과 칠할지.
 *
 * @param toggles 칸들 — 차례가 곧 말의 차례다
 * @returns `summary` 켜진 이름(「AI 뷰티 필터 외 1」, 다 꺼졌으면 「모두 끔」) · `active` 처음 값에서 하나라도 벗어났나
 */
export function summarizeToggles(toggles: readonly FilterToggleState[]): { summary: string; active: boolean } {
  const onLabels = toggles.filter((one) => one.on).map((one) => one.label);
  return {
    summary: summarizeSelection(onLabels, onLabels.map((one) => ({ value: one, label: one })), '모두 끔'),
    active: toggles.some((one) => one.on !== one.defaultOn),
  };
}

/** 요약 칩의 ×가 푸는 갈래 — 사이드바 섹션 하나씩이다 */
export type MomentFilterGroupKey = 'channel' | 'country' | 'language' | 'persona' | 'metricMin' | 'refine';

/**
 * 갈래 하나를 그 섹션의 「전체」로 되돌린 필터 — 요약 칩의 ×가 쓴다(섹션에서 「전체」를 누른 것과 같은 값).
 *
 * 🔑 채널 「전체」는 `platforms: []` 이고, 세부 채널 칩이 서는 화면이면 세부 칸도 처음 값으로 되돌린다(`handleChannelAllChipClick`).
 * 🔑 작성자 「전체」는 두 칸을 다 비운다 — 배열 꼴로 온 필터는 배열로, 낱값 꼴은 낱값으로 둔다.
 * ⚠️ 플랜 고정 국가는 여기서 다시 씌우지 않는다 — 화면의 `setFilter` 가 갱신마다 씌운다.
 * @param prev 지금 필터
 * @param key 푸는 갈래
 * @param options.channelSubtypes 세부 채널 칩이 서는 화면인가(섹션의 `channelSubtypeEnabled` 와 같은 값)
 * @returns 새 필터(`prev` 는 그대로)
 */
export function clearMomentFilterGroup<T extends Record<string, any>>(
  prev: T,
  key: MomentFilterGroupKey,
  { channelSubtypes = false }: { channelSubtypes?: boolean } = {},
): T {
  const base = (prev || {}) as Record<string, any>;
  switch (key) {
    case 'channel':
      return {
        ...base,
        platforms: [],
        ...(channelSubtypes ? {
          xPlatforms: DEFAULT_X_SUBTYPE_PLATFORMS,
          instagramPlatforms: DEFAULT_INSTAGRAM_SUBTYPE_PLATFORMS,
          tiktokAdTypes: DEFAULT_TIKTOK_AD_TYPE_CHIPS,
          tiktokAdBoosts: DEFAULT_TIKTOK_AD_BOOST_CHIPS,
        } : {}),
      } as unknown as T;
    case 'country':
      return { ...base, country: [] } as unknown as T;
    case 'language':
      return { ...base, languages: [] } as unknown as T;
    case 'persona': {
      const arrayMode = Array.isArray(base.adsEval) || Array.isArray(base.isOfficialAccount);
      return (arrayMode
        ? { ...base, adsEval: [], isOfficialAccount: [] }
        : { ...base, adsEval: null, isOfficialAccount: false }) as unknown as T;
    }
    case 'metricMin':
      return { ...base, metricMin: null } as unknown as T;
    case 'refine':
      return { ...base, refineText: '' } as unknown as T;
    default:
      return prev;
  }
}
